// Dependencies
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import Autoscroll from 'embla-carousel-auto-scroll';
import uuid from 'react-uuid';

// Styles
import SliderStyles from './slider.styles';

export default function Slider({
  children,
  slides,
  gap,
  autoscroll,
  arrows,
  dots,
  loop,
  interactable,
  breakpoints,
  className,
}) {
  const baseOptions = {
    slidesToScroll: 'auto',
    loop,
    watchDrag: interactable,
  };
  const moduleOptions = [];

  if (autoscroll)
    moduleOptions.push(Autoscroll({ speed: 1, stopOnInteraction: true }));

  const [emblaRef, emblaApi] = useEmblaCarousel(baseOptions, moduleOptions);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const onInit = useCallback(() => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <SliderStyles
      slides={slides}
      gap={gap}
      breakpoints={breakpoints}
      className={className}
    >
      <div className="slider-wrapper">
        <div className="slider">
          <div className="slider-viewport" ref={emblaRef}>
            <div className="slider-container">{children}</div>

            {!!arrows && (
              <>
                <button
                  type="button"
                  aria-label="Previous"
                  className="slider-prev"
                  onClick={scrollPrev}
                  disabled={prevBtnDisabled}
                />
                <button
                  type="button"
                  aria-label="Next"
                  className="slider-next"
                  onClick={scrollNext}
                  disabled={nextBtnDisabled}
                />
              </>
            )}

            {!!dots && (
              <div className="dots">
                {scrollSnaps.map((_, index) => (
                  <button
                    type="button"
                    className={`dot ${index === selectedIndex && 'active'}`}
                    onClick={() => scrollTo(index)}
                    key={uuid()}
                  >
                    &bull;
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </SliderStyles>
  );
}

Slider.propTypes = {
  children: PropTypes.node.isRequired,
  slides: PropTypes.number,
  gap: PropTypes.number,
  autoscroll: PropTypes.bool,
  arrows: PropTypes.bool,
  dots: PropTypes.bool,
  loop: PropTypes.bool,
  interactable: PropTypes.bool,
  breakpoints: PropTypes.shape({
    375: PropTypes.shape({}),
    768: PropTypes.shape({}),
    992: PropTypes.shape({}),
    1200: PropTypes.shape({}),
    1920: PropTypes.shape({}),
  }),
  className: PropTypes.string,
};

Slider.defaultProps = {
  slides: 2,
  gap: 2,
  autoscroll: false,
  arrows: false,
  dots: false,
  loop: true,
  interactable: true,
  breakpoints: {
    375: { slides: 2 },
    768: { slides: 2 },
    992: { slides: 2 },
    1200: { slides: 2 },
    1920: { slides: 2 },
  },
  className: '',
};
